import { useQuery } from '@tanstack/react-query'

import { createApiCall } from 'services'
import type { CertificateAdmin } from 'types'

/**
 * The ONLY statuses `POST /transfer/admin/init/:certificateId` accepts.
 *
 * `transfer-certificate-to-owner.use-case.ts` rejects anything that is not
 * `inactive` with a 400 (`should be in "Inactive" status`), and rejects the four
 * in-transfer states with a different 400 before that. `inactive` is the state a
 * certificate sits in between being minted and being claimed by an owner —
 * accepting a transfer is what moves it to `active`, so an `active` certificate
 * already HAS an owner and is not the brand's to hand out.
 *
 * Kept here rather than inlined at the call site because it is a server contract,
 * not a UI preference: if the use-case widens what it accepts, this is the one
 * place the picker and the row actions both read.
 */
export const ADMIN_TRANSFERABLE_STATUSES = ['inactive'] as const

type UseTransferableCertificatesOptions = {
  enabled?: boolean
  limit?: number
  /**
   * VRG-720 — narrow the fetch to these certificate ids (server-side, via
   * `/certificates/admin?certificateIds=`), still filtered to the transferable
   * statuses above. Used by the Leads surface, which knows exactly which
   * certificates a lead scanned and must offer those and nothing else.
   *
   * Server-side rather than filtering the result here, and the distinction is not
   * cosmetic: the unrestricted fetch is capped at `limit` (100). A brand with more
   * than 100 transferable certificates would hand back a page that need not contain
   * the lead's one at all, so a client-side filter would quietly produce an empty
   * picker for a certificate that exists and is transferable.
   *
   * An EMPTY array is a meaningful restriction -- "none" -- not an absent one, so it
   * disables the query rather than silently fetching everything. Passing `[]` and
   * getting the whole catalogue back is the failure this guards.
   */
  certificateIds?: string[] | null
}

type UseTransferableCertificatesResult = {
  data: CertificateAdmin[]
  isLoading: boolean
  isError: boolean
}

/**
 * Certificates this brand may admin-transfer, for the transfer modal's picker.
 *
 * Deliberately NOT `useCertificates` — that hook is Redux-saga backed and writes
 * into the shared `state.certificates.certificates` slice, which is the same slice
 * the Certificates table renders from. A filtered fetch through it would replace
 * the visible table with this modal's narrower result set (and the modal is mounted
 * unconditionally on that page, so it would race the table's own fetch on every
 * load). react-query keeps this result local to the modal.
 */
export const useTransferableCertificates = (
  options?: UseTransferableCertificatesOptions
): UseTransferableCertificatesResult => {
  const limit = options?.limit ?? 100
  const certificateIds = options?.certificateIds

  // An explicit empty restriction means "there is nothing to look up". Fetching on it
  // would return the unrestricted list, which is the opposite of what was asked.
  const restrictedToNothing = Array.isArray(certificateIds) && certificateIds.length === 0

  const query = useQuery({
    queryKey: ['certificates', 'transferable', limit, certificateIds ?? null],
    enabled: options?.enabled !== false && !restrictedToNothing,
    queryFn: async () => {
      const response = await createApiCall({
        method: 'GET',
        url: '/certificates/admin',
        params: {
          statuses: ADMIN_TRANSFERABLE_STATUSES,
          limit,
          offset: 0,
          ...(certificateIds && certificateIds.length > 0 ? {certificateIds} : {}),
        },
      })

      if (response.status !== 200) {
        throw new Error('Failed to fetch transferable certificates')
      }

      // `/certificates/admin` answers `{ data, total }`; tolerate a bare array too.
      const payload = response.data
      return (Array.isArray(payload) ? payload : payload?.data ?? []) as CertificateAdmin[]
    },
  })

  return {
    data: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
  }
}
